import { constant, difference, filter, isEmpty, reduce } from 'lodash/fp';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setGoogleAnalyticsCookie } from '@skippay/core/components/GoogleAnalytics';
import { loadScript, loadedScriptsSelector } from '@skippay/core/modules/externalScripts';
import { delay } from '@skippay/core/utils/promiseUtils';

import selfCareExternalScripts, {
	EXPONEA,
	FOXENTRY,
	GOOGLE_MAPS,
	GOOGLE_TAG_MANAGER,
	JARABOT,
	SENTRY,
	SMARTFORM,
} from 'utils/externalScripts';

const WaitForReload = React.lazy(() => delay(200000));

const optionalExternalScripts = [FOXENTRY, GOOGLE_MAPS, SMARTFORM, JARABOT] as const;

type MandatoryExternalScriptsNames = typeof EXPONEA | typeof GOOGLE_TAG_MANAGER | typeof SENTRY;

type OptionalExternalScriptsNames = (typeof optionalExternalScripts)[number];

// @ts-expect-error
const { allScripts, lazyScripts, trustedScripts } = reduce<{
	allScripts: string[];
	lazyScripts: string[];
	trustedScripts: string[];
}>(
	// @ts-expect-error
	(acc, { lazy, securedOnly, trusted }, name) => {
		if (lazy) {
			acc.lazyScripts.push(name);
		}
		if (trusted) {
			acc.trustedScripts.push(name);
		}
		if (!securedOnly) {
			acc.allScripts.push(name);
		}
		return acc;
	},
	{ allScripts: [], lazyScripts: [], trustedScripts: [] },
	selfCareExternalScripts
);

interface Props {
	secured?: boolean;
	excludedScripts?: MandatoryExternalScriptsNames[];
	includedScripts?: OptionalExternalScriptsNames[];
}

/**
 * Component dispatches events to externalScripts module to load 3rd party scripts and sets Google
 * Tag Manager cookie value indicating security level of page, so GTM administrators can accordingly
 * load 3rd party scripts via GTM.
 *
 * THIS COMPONENT MUST NOT BE LOADED IN MOBILE APP!
 * Mobile application is considered all secured and using ExternalScripts component there could
 * load untrusted 3rd party scripts and/or could reload the page in some scenarios.
 */
const ExternalScripts = React.memo(function ExternalScripts(props: Props) {
	const { secured, excludedScripts, includedScripts } = props;
	const dispatch = useDispatch();
	const loadedScripts = useSelector(loadedScriptsSelector);
	const untrustedLoaded = !isEmpty(difference(loadedScripts, trustedScripts));
	const [cookieConsents, setCookieConsents] = useState(
		typeof window !== 'undefined' ? window.skippay?.cookie?.getConsents() || {} : {}
	);
	useEffect(() => window.skippay?.cookie?.onConsentsChange(setCookieConsents), []);

	// layoutEffect is used to trigger reload as soon as possible
	useLayoutEffect(() => {
		if (secured && untrustedLoaded) {
			window.location.reload();
		} else {
			const scriptsToLoad = secured ? trustedScripts : allScripts;

			const excludeOptionalScripts = filter(
				(script: OptionalExternalScriptsNames) => !includedScripts?.includes(script),
				optionalExternalScripts
			);

			const scripts = filter(
				(script: MandatoryExternalScriptsNames) =>
					![...(excludedScripts || []), ...excludeOptionalScripts].includes(script),
				difference(scriptsToLoad, lazyScripts)
			);

			// @ts-expect-error loadScript is redux action
			dispatch(loadScript(scripts));
			setGoogleAnalyticsCookie(!!secured);
		}
	}, [cookieConsents, secured, untrustedLoaded, excludedScripts, dispatch]);

	return secured && untrustedLoaded ? <WaitForReload /> : null;
});

// this component is no-op during SSR
export default typeof window === `undefined`
	? constant(null)
	: process.env.MOB_APP_BUILD
		? () => {
				throw new Error(
					`The self-care "ExternalScripts" component must not be loaded in mobile application.`
				);
			}
		: ExternalScripts;
